import { useState, useEffect, useCallback } from 'react';
import { helpers } from 'modules/hub/components/AMAChat/utils/helpers';
import labels from 'modules/hub/components/AMAChat/constants/labels/ama';
import { buildConversationQueryParams } from './utils';
import { initSessionForUser, clearSessionId, getCurrentSessionId, updateSessionId, } from '../../utils/sessionStorage';
import { getCompanyIdFromUrl } from '../../utils/companyIdFromUrl';
const noop = async () => undefined;
// Fill the `{company}` placeholder in the starter questions with the current
// company's display name (e.g. "HypoVereinsbank"). Falls back to "Stepstone"
// when no name is available so a raw `{company}` token never reaches the UI.
export const interpolateCompany = (text, companyName) => text.replace(/\{company\}/g, companyName || 'Stepstone');
export const getStarterQuestions = (lang, companyName) => {
    const suggestions = labels[lang].entrypointInputSuggestions;
    return Object.values(suggestions).map((q) => interpolateCompany(q, companyName));
};
const getRandomStarterQuestions = (lang, companyName, count = 3) => {
    const allQuestions = getStarterQuestions(lang, companyName);
    const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
};
// Initial messages with deterministic starter questions (no random order)
// to avoid hydration mismatch between server and client
const getInitialMessages = (lang, companyName) => [
    {
        id: 123455,
        role: 'customisedAssistant',
        content: labels[lang].betaIntroMessage,
        meta: {},
    },
    {
        id: 123456,
        role: 'customisedAssistant',
        content: labels[lang].welcomeQuestion,
        meta: {
            actions: [
                {
                    starterButtons: getStarterQuestions(lang, companyName), // Use deterministic order initially
                },
            ],
        },
    },
];
// Mirror of the server-side DomainErrorCode enum (kineticApiService.ts).
// Duplicated here because frontend cannot import from server modules.
// Keep values in sync with the Kinetic API swagger spec.
var DomainErrorCode;
(function (DomainErrorCode) {
    DomainErrorCode[DomainErrorCode["MESSAGE_LIMIT_PER_SESSION_EXCEEDED"] = 17] = "MESSAGE_LIMIT_PER_SESSION_EXCEEDED";
    DomainErrorCode[DomainErrorCode["MESSAGE_LIMIT_PER_DAY_EXCEEDED"] = 18] = "MESSAGE_LIMIT_PER_DAY_EXCEEDED";
    DomainErrorCode[DomainErrorCode["AMA_BUDGET_EXCEEDED"] = 19] = "AMA_BUDGET_EXCEEDED";
})(DomainErrorCode || (DomainErrorCode = {}));
const limitUntilKey = (companyId) => `limitDisabledUntil_${companyId}`;
const limitTypeKey = (companyId) => `limitErrorType_${companyId}`;
// First millisecond of the next calendar month in UTC. Mirrors the backend's
// month-aligned reset (monthly_ai_cost row keyed by year+month, UTC).
const nextMonthStartMs = () => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0);
};
export const useMya = (props) => {
    const lang = props?.lang || 'de';
    // Display name (e.g. "HypoVereinsbank") used ONLY to interpolate the starter
    // questions. Resolved once here so SSR and the client reshuffle use the same
    // value (no hydration mismatch). NOTE: this is the human-readable name, not
    // the identifier — the backend `metadata.company` field is keyed on the URL
    // slug (see sendMessage below), so the two must stay separate.
    const companyDisplayName = props?.companyName || getCompanyIdFromUrl();
    const [messages, setMessages] = useState(getInitialMessages(lang, companyDisplayName));
    const [sessionToken, setSessionToken] = useState('123456'); // Deterministic initial value
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    // Shuffle starter questions, generate session token, and load conversation history on client-side only
    useEffect(() => {
        setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages[1]?.meta?.actions?.[0]) {
                updatedMessages[1].meta.actions[0].starterButtons =
                    getRandomStarterQuestions(lang, companyDisplayName, 3);
            }
            return updatedMessages;
        });
        setSessionToken(helpers.rand().toString());
        // Load conversation history if session exists
        const loadConversationHistory = async () => {
            try {
                const companyId = getCompanyIdFromUrl();
                // Resolve candidate ID, detect user transitions, and get the existing session
                // (null if this is the user's first message — backend will assign a session ID).
                const existingSessionId = await initSessionForUser(companyId);
                setCurrentSessionId(existingSessionId);
                // Always fetch history — the backend returns all turns for this candidate+company,
                // or 204 if no conversation exists yet.
                const queryParams = buildConversationQueryParams(companyId);
                const response = await fetch(`/hub/${companyId}/api/ama/conversation?${queryParams.toString()}`);
                const data = await response.json();
                if (data.success &&
                    data.conversations &&
                    data.conversations.length > 0) {
                    // Remove initial welcome messages and add conversation history
                    const historyMessages = [];
                    data.conversations.forEach((conv) => {
                        historyMessages.push({
                            id: helpers.rand(),
                            role: 'user',
                            content: conv.question,
                            turn: conv.turn,
                            meta: {},
                        });
                        historyMessages.push({
                            id: helpers.rand(),
                            role: 'assistant',
                            content: conv.answer,
                            turn: conv.turn,
                            feedback: conv.feedback,
                            feedbackReasonType: conv.feedbackReasonType,
                            feedbackReason: conv.feedbackReason,
                            reportReasonType: conv.reportReasonType,
                            reportReason: conv.reportReason,
                            meta: {},
                        });
                    });
                    // Add welcome message with starter buttons at the end
                    historyMessages.push({
                        id: helpers.rand(),
                        role: 'customisedAssistant',
                        content: labels[lang].welcomeQuestion,
                        meta: {
                            actions: [
                                {
                                    starterButtons: getRandomStarterQuestions(lang, companyDisplayName, 3),
                                },
                            ],
                        },
                    });
                    setMessages([
                        getInitialMessages(lang, companyDisplayName)[0],
                        ...historyMessages,
                    ]);
                }
                // Re-append a limit error message if a lock is still active (page refresh).
                // Done here so it's part of the same render cycle as history messages.
                const storedType = localStorage.getItem(limitTypeKey(companyId));
                if (storedType) {
                    const limitContent = storedType === 'session'
                        ? labels[lang].sessionLimitMessage
                        : storedType === 'budget'
                            ? labels[lang].budgetLimitMessage
                            : labels[lang].dailyLimitMessage;
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: helpers.rand(),
                            role: 'limitError',
                            content: limitContent,
                            meta: {},
                        },
                    ]);
                }
                setIsLoadingHistory(false);
            }
            catch (error) {
                console.error('Error loading conversation history:', error);
                setIsLoadingHistory(false);
            }
        };
        loadConversationHistory();
    }, [lang, companyDisplayName]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInputDisabled, setIsInputDisabled] = useState(false);
    // Restore input-disabled state from localStorage on mount (survives page refresh).
    // Computes remaining lock duration and registers a timer to auto-unlock.
    useEffect(() => {
        const companyId = getCompanyIdFromUrl();
        const stored = localStorage.getItem(limitUntilKey(companyId));
        if (!stored) {
            // No active lock — clean up any orphaned type key left by older code
            // that removed limitDisabledUntil without removing limitErrorType.
            localStorage.removeItem(limitTypeKey(companyId));
            return;
        }
        const disabledUntil = parseInt(stored, 10);
        const remaining = disabledUntil - Date.now();
        if (remaining <= 0) {
            localStorage.removeItem(limitUntilKey(companyId));
            localStorage.removeItem(limitTypeKey(companyId));
            return;
        }
        setIsInputDisabled(true);
        const timer = setTimeout(() => {
            setIsInputDisabled(false);
            localStorage.removeItem(limitUntilKey(companyId));
            localStorage.removeItem(limitTypeKey(companyId));
            setMessages((prev) => prev.filter((msg) => msg.role !== 'limitError'));
        }, remaining);
        return () => clearTimeout(timer);
    }, []);
    // Called after a session dropout clears the old session — resets tracking state
    // so the next message is sent without a session_id and backend assigns a fresh one.
    const refreshSessionId = useCallback(() => {
        setCurrentSessionId(null);
    }, []);
    return {
        isLoading: isLoading || isLoadingHistory,
        isInputDisabled,
        isLoadingHistory,
        sendMessage: async ({ message }) => {
            const userMsg = {
                id: helpers.rand(),
                role: 'user',
                content: message,
                meta: {},
            };
            // Remove the entire message with starter buttons when user sends a message
            setMessages((prev) => prev.filter((msg) => !msg.meta?.actions?.[0]?.starterButtons));
            // Add user message immediately
            setMessages((prev) => [...prev, userMsg]);
            setIsLoading(true);
            try {
                const companyId = getCompanyIdFromUrl();
                const storyId = props?.storyId;
                // Read the stored session ID — null on first message (backend will assign one)
                const sessionId = getCurrentSessionId({ companyId });
                // Call the server-side API endpoint
                // API must be under /hub/:companyId to match Nginx routing
                const response = await fetch(`/hub/${companyId}/api/ama/chat`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...(sessionId ? { session_id: sessionId } : {}),
                        company_id: companyId,
                        user_query: message,
                        // Context is built on the backend from scraped data.
                        // `company` is the URL slug identifier (NOT the display name) —
                        // the backend keys grounding/context on it, so it must stay the slug.
                        metadata: {
                            company: companyId,
                            storyId: storyId,
                            source: 'company-hub-ama',
                        },
                    }),
                });
                // Detect session expiry from HTTP status
                if (response.status === 401 || response.status === 403) {
                    props?.onSessionExpired?.();
                }
                const data = await response.json();
                // Detect session expiry from error message
                const sessionExpiredPattern = /session.*expir|expir.*session/i;
                if (!data.success && sessionExpiredPattern.test(data.error || '')) {
                    props?.onSessionExpired?.();
                }
                if (data.success && data.content) {
                    // If the API assigned a different session ID, persist it so all
                    // subsequent requests use the correct Kinetic-registered UUID.
                    if (data.session_id && data.session_id !== sessionId) {
                        updateSessionId(companyId, data.session_id);
                        setCurrentSessionId(data.session_id);
                    }
                    const assistantMsg = {
                        id: helpers.rand(),
                        role: 'assistant',
                        content: data.content,
                        turn: data.turn,
                        meta: {},
                    };
                    const starterButtonsMsg = {
                        id: helpers.rand(),
                        role: 'customisedAssistant',
                        content: labels[lang].welcomeQuestion,
                        meta: {
                            actions: [
                                {
                                    starterButtons: getRandomStarterQuestions(lang, companyDisplayName, 3),
                                },
                            ],
                        },
                    };
                    setMessages((prev) => [...prev, assistantMsg, starterButtonsMsg]);
                }
                else {
                    const isSessionLimit = data.errorCode ===
                        DomainErrorCode.MESSAGE_LIMIT_PER_SESSION_EXCEEDED;
                    const isDailyLimit = data.errorCode === DomainErrorCode.MESSAGE_LIMIT_PER_DAY_EXCEEDED;
                    const isBudgetExceeded = data.errorCode === DomainErrorCode.AMA_BUDGET_EXCEEDED;
                    if (isSessionLimit || isDailyLimit || isBudgetExceeded) {
                        // Disable input until the limit resets:
                        // session limit → 30 minutes, daily limit → midnight,
                        // budget cap → first millisecond of next UTC month.
                        const disabledUntil = isSessionLimit
                            ? Date.now() + 30 * 60 * 1000
                            : isDailyLimit
                                ? new Date().setHours(24, 0, 0, 0)
                                : nextMonthStartMs();
                        const limitType = isSessionLimit
                            ? 'session'
                            : isDailyLimit
                                ? 'daily'
                                : 'budget';
                        const limitMessage = isSessionLimit
                            ? labels[lang].sessionLimitMessage
                            : isDailyLimit
                                ? labels[lang].dailyLimitMessage
                                : labels[lang].budgetLimitMessage;
                        localStorage.setItem(limitUntilKey(companyId), String(disabledUntil));
                        localStorage.setItem(limitTypeKey(companyId), limitType);
                        setIsInputDisabled(true);
                        // Timer is not cleaned up on unmount — state updates on unmounted
                        // components are no-ops in React 18, so this is safe.
                        setTimeout(() => {
                            setIsInputDisabled(false);
                            localStorage.removeItem(limitUntilKey(companyId));
                            localStorage.removeItem(limitTypeKey(companyId));
                            setMessages((prev) => prev.filter((msg) => msg.role !== 'limitError'));
                        }, disabledUntil - Date.now());
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: helpers.rand(),
                                role: 'limitError',
                                content: limitMessage,
                                meta: {},
                            },
                        ]);
                    }
                    else {
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: helpers.rand(),
                                role: 'assistant',
                                content: `${labels[lang].errorHeading}\n\n${labels[lang].technicalErrorMessage}`,
                                meta: {},
                            },
                        ]);
                    }
                }
            }
            catch (error) {
                console.error('Error calling AMA API:', error);
                const errorMsg = {
                    id: helpers.rand(),
                    role: 'assistant',
                    content: `${labels[lang].errorHeading}\n\n${labels[lang].technicalErrorMessage}`,
                    meta: {},
                };
                setMessages((prev) => [...prev, errorMsg]);
            }
            finally {
                setIsLoading(false);
            }
        },
        currentSessionId,
        refreshSessionId,
        conversationId: 123456,
        createNewConversation: () => {
            // Reset session for new conversation
            clearSessionId({ companyId: getCompanyIdFromUrl() });
            // Reset messages to initial state
            setMessages(getInitialMessages(lang, companyDisplayName));
            setIsLoadingHistory(false);
        },
        rejoinConversation: noop,
        startSocket: noop,
        messages,
        sessionToken,
    };
};
